// Player history
// This will go through all the leagues/teams that a player used to play for
// and show all their stats.

import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import YouTube from 'react-youtube'

import './Players.css'

import StatsAndStandingsHome from '../../shared/components/Navigation/StatsAndStandingsHome'
import ErrorModal from '../../shared/components/UIElements/ErrorModal'
import LoadingSpinner from '../../shared/components/UIElements/LoadingSpinner'
import Footer from '../../shared/components/UIElements/Footer'
import Modal from '../../shared/components/UIElements/Modal'
import AddPhotoOrVideosNote from './AddPhotoOrVideosNote'

const getYouTubeID = require('get-youtube-id')

const PlayerHistory = () => {
	const [error, setError] = useState()
	const [isLoading, setIsLoading] = useState(false)
	const [loadedRosters, setLoadedRosters] = useState()
	const [firstName, setFirstName] = useState()
	const [lastName, setLastName] = useState()
	const [middleInitial, setMiddleInitial] = useState()
	const [photo, setPhoto] = useState()
	const [listOfVideos, setListOfVideos] = useState()
	const [ids, setIds] = useState([])
	const [onlyBeenGoalie, setOnlyBeenGoalie] = useState()
	const [hasPlayedOutAndBeenGoalie, setHasPlayedOutAndBeenGoalie] = useState()
	const [neverPlayedGoalie, setNeverPlayedGoalie] = useState()
	const [processDone, setProcessDone] = useState(false)
	const [goalieStats, setGoalieStats] = useState(false)
	const [goalieAndNonGoalieStats, setGoalieAndNonGoalieStats] = useState(false)
	const [showInstructionsModal, setShowInstructionsModal] = useState(false)
	const [goalieStatsByRosterSeason, setGoalieStatsByRosterSeason] = useState()
	const [seasonsWherePlayerWasGoalie, setSeasonsWherePlayerWasGoalie] =
		useState()
	const [seasonsWherePlayerWasNotGoalie, setSeasonsWherePlayerWasNotGoalie] =
		useState()
	const [isMobile440, setIsMobile440] = useState(false)

	const playerId = useParams().playerId

	/*
		Get the player's history.
	*/
	useEffect(() => {
		const sendRequest = async () => {
			setIsLoading(true)

			try {
				const response = await fetch(
					`${process.env.REACT_APP_BACKEND_URL}/player/${playerId}/history`,
				)

				const responseData = await response.json()

				if (!response.ok) {
					throw new Error(responseData.message)
				}

				setLoadedRosters(responseData.listOfRosters)
				setFirstName(responseData.firstName)
				setLastName(responseData.lastName)
				setPhoto(responseData.photo)
				setMiddleInitial(responseData.middleInitial)
				setListOfVideos(responseData.listOfVideos)
			} catch (err) {
				setError(err.message)
			}

			setIsLoading(false)
		}

		sendRequest()
	}, [playerId])
	//
	//
	//
	useEffect(() => {
		const checkMobile = () => {
			setIsMobile440(window.matchMedia('(max-width: 440px)').matches)
		}

		checkMobile()

		window.addEventListener('resize', checkMobile)

		return () => {
			window.removeEventListener('resize', checkMobile)
		}
	}, [])

	/*
		Create the YouTube IDs when the video list changes.
	*/
	useEffect(() => {
		if (!listOfVideos || listOfVideos.length === 0) {
			setIds([])
			return
		}

		const newIds = listOfVideos.map((video) => getYouTubeID(video.videoURL))

		setIds(newIds)
	}, [listOfVideos])

	/*
		Determine whether the player has played goalie, skater,
		or both.
	*/
	useEffect(() => {
		if (!loadedRosters || processDone) {
			return
		}

		let wasGoalie = 0
		let wasOut = 0

		loadedRosters.forEach((roster) => {
			if (roster.isGoalie) {
				wasGoalie += 1
			} else {
				wasOut += 1
			}
		})

		if (wasGoalie > 0 && wasOut === 0) {
			setOnlyBeenGoalie(true)
		}

		if (wasGoalie > 0 && wasOut > 0) {
			setHasPlayedOutAndBeenGoalie(true)
		}

		if (wasGoalie === 0 && wasOut > 0) {
			setNeverPlayedGoalie(true)
		}

		setProcessDone(true)
	}, [loadedRosters, processDone])

	/*
		Get goalie-only statistics.
	*/
	useEffect(() => {
		if (!processDone || !onlyBeenGoalie || goalieStats) {
			return
		}

		const getGoalieStats = async () => {
			try {
				const response = await fetch(
					`${process.env.REACT_APP_BACKEND_URL}/player/goalieStats/${playerId}`,
				)

				const responseData = await response.json()

				if (!response.ok) {
					throw new Error(responseData.message)
				}

				setGoalieStatsByRosterSeason(responseData.goalieStatsByRosterSeason)
				setGoalieStats(true)
			} catch (err) {
				setError(err.message)
			}
		}

		getGoalieStats()
	}, [goalieStats, onlyBeenGoalie, playerId, processDone])

	/*
		Get combined goalie and skater statistics.
	*/
	useEffect(() => {
		if (!processDone || !hasPlayedOutAndBeenGoalie || goalieAndNonGoalieStats) {
			return
		}

		const getGoalieAndNonGoalieStats = async () => {
			try {
				const response = await fetch(
					`${process.env.REACT_APP_BACKEND_URL}/player/goalieAndNonGoalieStats/${playerId}`,
				)

				const responseData = await response.json()

				if (!response.ok) {
					throw new Error(responseData.message)
				}

				setSeasonsWherePlayerWasGoalie(responseData.seasonsWherePlayerWasGoalie)

				setSeasonsWherePlayerWasNotGoalie(
					responseData.seasonsWherePlayerWasNotGoalie,
				)

				setGoalieAndNonGoalieStats(true)
			} catch (err) {
				setError(err.message)
			}
		}

		getGoalieAndNonGoalieStats()
	}, [
		goalieAndNonGoalieStats,
		hasPlayedOutAndBeenGoalie,
		playerId,
		processDone,
	])

	const errorHandler = () => {
		setError(null)
	}

	const openInstructionModal = () => {
		setShowInstructionsModal(true)
	}

	const okHandler = () => {
		setShowInstructionsModal(false)
	}

	const getGoalsAgainstAverage = (team) => {
		const gamesPlayed = Number(team.goalieGamesPlayed)
		const goalsAgainst = Number(team.goalsAgainst)

		if (!gamesPlayed || Number.isNaN(goalsAgainst)) {
			return ''
		}

		return (goalsAgainst / gamesPlayed).toFixed(3)
	}

	const getPlayerName = () => {
		return [firstName, middleInitial, lastName].filter(Boolean).join(' ')
	}

	const playerName = getPlayerName()

	// All seasons where the player was registered as a goalie
	const goalieSeasons = (loadedRosters || []).filter(
		(team) => team.isGoalie === true,
	)

	// All non-goalie seasons, plus goalie seasons with goals or assists
	const skaterSeasons = (loadedRosters || []).filter(
		(team) =>
			team.isGoalie === false ||
			(team.isGoalie === true &&
				(Number(team.goals || 0) > 0 || Number(team.assists || 0) > 0)),
	)

	const hasGoalieSeasons = goalieSeasons.length > 0
	const hasSkaterSeasons = skaterSeasons.length > 0

	return (
		<div style={{ overflowX: 'hidden' }}>
			<React.Fragment>
				<Modal
					show={showInstructionsModal}
					header='Add player photo or video(s)'
					footerClass='place-item__modal-actions'
					footer={
						<React.Fragment>
							<p className='modalText'>
								Email your picture and any videos you may have to
								matt@shireystatsandstandings.com
							</p>

							<br />

							<p className='modalText'>
								Please include you or your child&apos;s name and team. When
								sending a video, please also include a description.
							</p>

							<br />

							<button
								type='button'
								className='okButton instructionOKButton'
								onClick={okHandler}
							>
								OKAY
							</button>
						</React.Fragment>
					}
				/>

				<StatsAndStandingsHome />

				<ErrorModal error={error} onClear={errorHandler} />

				{isLoading && (
					<div className='center'>
						<LoadingSpinner />
					</div>
				)}

				{!isLoading && loadedRosters && (
					<div className='Roster playerHistorySection'>
						{/* Desktop and tablet profile header */}
						<div className='playerHistoryDesktopHeader'>
							{photo && (
								<div className='photoDiv'>
									<img
										src={`${process.env.REACT_APP_AWS_BUCKET}/${photo}`}
										alt={playerName}
										className='playerPhoto'
									/>
								</div>
							)}

							{!photo && (
								<button
									type='button'
									className='instructionButton'
									onClick={openInstructionModal}
								>
									<AddPhotoOrVideosNote />
								</button>
							)}

							<h1>{playerName}</h1>

							<h3 className='h3Center'>Player History</h3>
						</div>

						{/* Mobile-only profile header */}
						<div className='playerHistoryMobileHeader'>
							<div className='playerHistoryMobileHeader__card'>
								{photo ? (
									<img
										src={`${process.env.REACT_APP_AWS_BUCKET}/${photo}`}
										alt={playerName}
										className='playerHistoryMobileHeader__photo'
									/>
								) : (
									<div className='playerHistoryMobileHeader__silhouetteContainer'>
										<div className='playerHistoryMobileHeader__silhouette'>
											<div className='playerHistoryMobileHeader__silhouetteHead'></div>

											<div className='playerHistoryMobileHeader__silhouetteBody'></div>
										</div>
									</div>
								)}

								<div className='playerHistoryMobileHeader__name'>
									{playerName}
								</div>

								<div className='playerHistoryMobileHeader__divider'></div>

								<div className='playerHistoryMobileHeader__title'>
									Player History
								</div>
							</div>
						</div>

						{/* Goalie history */}
						{hasGoalieSeasons && (
							<div>
								<div className='playerHistoryMobileTableCard playerHistoryMobileTableCard--goalie'>
									<table id='playerHistoryTable' className='goalieHistoryTable'>
										<thead>
											<tr>
												<th>Team</th>
												<th>Year</th>
												<th>#</th>
												<th>GP</th>
												<th>W</th>
												<th>L</th>
												<th>Ties</th>
												<th>SO</th>
												<th>GAA</th>
											</tr>
										</thead>

										<tbody>
											{goalieSeasons.map((team, key) => (
												<tr key={team._id || key}>
													<td className='goalieHistoryTable__team'>
														<Link
															className='playerHistoryTeamLink'
															to={`/league/${team.leagueId}/${team.session}/${team.year}/${team.teamName}`}
														>
															<span className='playerHistoryTeamName'>
																{team.teamName}
															</span>

															<span className='playerHistoryLeagueName'>
																{team.leagueName}
															</span>
														</Link>
													</td>

													<td>{team.year}</td>
													<td>{team.number}</td>
													<td>{team.goalieGamesPlayed || 0}</td>
													<td>
														{Number(team.year) >= 2026
															? team.goalieWins || 0
															: team.wins || 0}
													</td>

													<td>
														{Number(team.year) >= 2026
															? team.goalieLosses || 0
															: team.losses || 0}
													</td>

													<td>
														{Number(team.year) >= 2026
															? team.goalieTies || 0
															: team.ties || 0}
													</td>

													<td>
														{Number(team.year) >= 2026
															? team.goalieShutouts || 0
															: team.shutouts || 0}
													</td>
													<td>{getGoalsAgainstAverage(team)}</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>

								<p className='asterisk' style={{ textAlign: 'center' }}>
									Goalie statistics by season
								</p>
							</div>
						)}

						{/* Skater-only history */}
						{neverPlayedGoalie && (
							<div className='playerHistoryMobileTableCard playerHistoryMobileTableCard--skater'>
								<table id='roster'>
									<thead>
										<tr>
											<th>Team</th>
											<th>Year</th>
											<th>#</th>
											<th>G</th>
											<th>A</th>
											{/* <th>PG</th>
											<th>PA</th> */}
											<th>Total</th>
											<th>GWG</th>
											<th>PIM</th>
										</tr>
									</thead>

									<tbody>
										{loadedRosters.map((team, key) => {
											const totalPoints =
												Number(team.goals || 0) +
												Number(team.assists || 0) +
												Number(team.playoffGoals || 0) +
												Number(team.playoffAssists || 0)

											return (
												<tr key={team._id || key}>
													<td>
														<Link
															className='playerHistoryTeamLink'
															to={`/league/${team.leagueId}/${team.session}/${team.year}/${team.teamName}`}
														>
															<span className='playerHistoryTeamName'>
																{team.teamName}
															</span>

															<span className='playerHistoryLeagueName'>
																{team.leagueName}
															</span>
														</Link>
													</td>

													<td>{team.year}</td>
													<td>{team.number}</td>
													<td>{team.goals + team.playoffGoals}</td>
													<td>{team.assists + team.playoffAssists}</td>
													{/* <td>{team.playoffGoals}</td>
													<td>{team.playoffAssists}</td> */}
													<td>{totalPoints}</td>
													<td>{team.gwg}</td>
													<td>{team.pim}</td>
												</tr>
											)
										})}
									</tbody>
								</table>
							</div>
						)}

						{/* Offensive statistics for players who have been goalies */}
						{hasGoalieSeasons && hasSkaterSeasons && (
							<div>
								<div className='playerHistoryMobileTableCard playerHistoryMobileTableCard--skater'>
									<table id='roster'>
										<thead>
											<tr>
												<th>Team</th>
												<th>Year</th>
												<th>#</th>
												<th>G</th>
												<th>A</th>
												<th>Total</th>
												<th>GWG</th>
												<th>PIM</th>
											</tr>
										</thead>

										<tbody>
											{skaterSeasons.map((team, key) => {
												const goals =
													Number(team.goals || 0) +
													Number(team.playoffGoals || 0)

												const assists =
													Number(team.assists || 0) +
													Number(team.playoffAssists || 0)

												const totalPoints = goals + assists

												return (
													<tr key={team._id || key}>
														<td>
															<Link
																className='playerHistoryTeamLink'
																to={`/league/${team.leagueId}/${team.session}/${team.year}/${team.teamName}`}
															>
																<span className='playerHistoryTeamName'>
																	{team.teamName}
																</span>

																<span className='playerHistoryLeagueName'>
																	{team.leagueName}
																</span>
															</Link>
														</td>

														<td>{team.year}</td>
														<td>{team.number}</td>
														<td>{goals}</td>
														<td>{assists}</td>
														<td>{totalPoints}</td>
														<td>{team.gwg || 0}</td>
														<td>{team.pim || 0}</td>
													</tr>
												)
											})}
										</tbody>
									</table>
								</div>
								<p className='asterisk' style={{ textAlign: 'center' }}>
									Offensive statistics by season
								</p>
							</div>
						)}

						{/* Video highlights */}
						{!isLoading && listOfVideos && listOfVideos.length > 0 && (
							<div className='section pt-5 playerVideos'>
								<div
									style={{
										overflowX: 'hidden',
									}}
								>
									<div className='row'>
										<div className='col text-center mb-0'>
											<div className='playerHistoryVideoHeading'>
												<div className='playerHistoryVideoHeading__top'>
													<span className='playerHistoryVideoHeading__line'></span>

													<span className='playerHistoryVideoHeading__icon'>
														<span className='playerHistoryVideoHeading__screen'>
															<span className='playerHistoryVideoHeading__play'></span>
														</span>
													</span>

													<span className='playerHistoryVideoHeading__line'></span>
												</div>

												<h1 className='text-sh-dark display-2'>
													Check it out!
												</h1>
											</div>
										</div>
									</div>

									<div className='row'>
										<div className='col-lg-3 col-sm-3'></div>

										<div className='col-lg-6 col-sm-6'>
											{listOfVideos.map((video, key) => {
												return (
													<div
														key={video._id || key}
														className='playerHistoryVideo'
													>
														<h1
															style={{
																color: 'red',
															}}
														>
															{video.videoTitle}
														</h1>

														<p
															className='caption'
															style={{
																textAlign: 'center',
																color: 'black',
															}}
														>
															{video.videoCaption}
														</p>

														<YouTube videoId={ids[key]} />
													</div>
												)
											})}
										</div>

										<div className='col-lg-3 col-sm-3'></div>
									</div>
								</div>
							</div>
						)}
					</div>
				)}

				<div style={{ marginTop: '13rem' }}>
					<Footer />
				</div>
			</React.Fragment>
		</div>
	)
}

export default PlayerHistory
